import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarPlus,
  FaExternalLinkAlt,
  FaHeart,
  FaMapMarkerAlt,
  FaRegHeart,
  FaShareAlt,
  FaTimes,
} from "react-icons/fa";
import {
  downloadIcs,
  fmtLongDate,
  fmtTimeRange,
  shareEvent, eventEmoji } from "./eventsApi";
import "./CommunityEvents.css";

/* =========================================================
   EventDetailsSheet   same anatomy as the universal business
   details card so users already know how to use it:
   flyer viewer (up to 3), date/time/venue rows, purple
   "Take me there", RSVP link, Save / Share / Add to calendar.

   Reuses ci-sheetwrap / ci-sheet / ci-sheet__* classes from
   CommunityShowcase.css (always loaded on showcase pages),
   plus cev-* extras from CommunityEvents.css.
   ========================================================= */

export default function EventDetailsSheet({
  event = null,
  onClose = null,
  isEventSaved = () => false,
  onToggleSave = null,
}) {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    setSlide(0);
  }, [event?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!event) return null;

  const images = Array.isArray(event.image_urls)
    ? event.image_urls.filter(Boolean).slice(0, 3)
    : [];
  const saved = isEventSaved(event.id);

  const openMap = () => {
    if (event.map_url) {
      window.open(event.map_url, "_blank", "noopener");
      return;
    }
    const query =
      [event.venue_name, event.address].filter(Boolean).join(", ") ||
      `${event.title} San Antonio`;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener"
    );
  };

  return createPortal(
    <div
      className="ci-sheetwrap"
      role="dialog"
      aria-modal="true"
      aria-label={event.title}
      onClick={(e) => {
        if (e.target === e.currentTarget && typeof onClose === "function") {
          onClose();
        }
      }}
    >
      <div className="ci-sheet">
        <button
          type="button"
          className="ci-sheet__x"
          onClick={onClose}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        <div className={`cev-viewer ${images.length ? "has-img" : "ci-ph-1"}`}>
          {images.length ? (
            <img
              src={images[slide]}
              alt={`${event.title} flyer ${slide + 1}`}
            />
          ) : (
            <span aria-hidden="true">{event.icon || eventEmoji(event)}</span>
          )}

          {images.length > 1 && (
            <>
              <span className="cev-viewer__n">
                {slide + 1} / {images.length}
              </span>
              <span className="cev-viewer__dots">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={i === slide ? "is-on" : ""}
                    onClick={() => setSlide(i)}
                    aria-label={`Flyer ${i + 1}`}
                  />
                ))}
              </span>
            </>
          )}
        </div>

        <span className="cev-sheet__badge">{fmtLongDate(event.event_date)}</span>

        <h2 className="ci-sheet__nm">{event.title}</h2>

        {fmtTimeRange(event) && (
          <p className="ci-sheet__tg">{fmtTimeRange(event)}</p>
        )}

        {(event.venue_name || event.address) && (
          <p className="ci-sheet__row">
            <strong>Where:</strong>{" "}
            {[event.venue_name, event.address].filter(Boolean).join(", ")}
          </p>
        )}

        {event.organizer_name && (
          <p className="ci-sheet__row">
            <strong>Hosted by:</strong> {event.organizer_name}
          </p>
        )}

        {event.description && (
          <p className="ci-sheet__ad">{event.description}</p>
        )}

        <button type="button" className="ci-sheet__takeme" onClick={openMap}>
          <FaMapMarkerAlt aria-hidden="true" /> Take me there
        </button>

        {event.ticket_url && (
          <a
            className="cev-rsvp"
            href={event.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaExternalLinkAlt aria-hidden="true" /> RSVP / get tickets
          </a>
        )}

        <div className="ci-sheet__acts">
          <button
            type="button"
            className={`ci-sheet__act ${saved ? "is-on" : ""}`}
            onClick={() => {
              if (typeof onToggleSave === "function") onToggleSave(event);
            }}
            aria-pressed={saved}
          >
            {saved ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
            {saved ? "Saved" : "Save"}
          </button>

          <button
            type="button"
            className="ci-sheet__act"
            onClick={() => shareEvent(event)}
          >
            <FaShareAlt aria-hidden="true" /> Share
          </button>

          <button
            type="button"
            className="ci-sheet__act"
            onClick={() => downloadIcs(event)}
          >
            <FaCalendarPlus aria-hidden="true" /> Add to calendar
          </button>
        </div>

        <p className="ci-sheet__disc">
          Community events are posted by local organizers and reviewed by
          GoOurHub before they appear. Details can change   check with the
          organizer before heading out.
        </p>
      </div>
    </div>
  , document.body);
}
